<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { maskCurrency } from "@/common/utils/mask";

import type { PlanType, PlanPayloadType } from "@/common/types/plan";

import { baseInput, baseSelect, baseTextarea } from "@/common/components";

const { t, tm } = useI18n();

const emit = defineEmits(["update:data", "validate"]);

const props = defineProps<{
  data: PlanType | null;
  isPatch?: boolean;
  isEdit?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  description: "",
  currency: "BRL",
  price: 0,
  frequency: 1,
  free: false,
  active: true,
};

const payload = ref<PlanPayloadType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

const activeOptions = computed(() => [
  { option: t("common.labels.active"), value: true },
  { option: t("common.labels.inactive"), value: false },
]);

const freeOptions = computed(() => [
  { option: t("common.labels.yes"), value: true },
  { option: t("common.labels.not"), value: false },
]);

const currencyOptions = computed(() =>
  Object.entries(tm("enums.currencies") as Record<string, string>).map(
    ([value, option]) => ({
      option,
      value,
    })
  )
);

function validate() {
  const free = ["active", "free", "price", "currency", "description"];

  Object.keys(payload.value).forEach((key) => {
    delete errors.value[key];

    if (!payload.value[key] && !free.includes(key))
      errors.value[key] = t("error.required");
  });

  emit("validate", Object.keys(errors.value).length === 0);
}

function update(key: keyof typeof payload.value) {
  validate();

  if (payload.value.free) {
    payload.value.price = 0;
  }

  if (props.isPatch) {
    if (payload.value[key] === props.data[key]) return;

    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const {
    name,
    description,
    price: priceDecimal,
    frequency,
    free,
    active,
    currency,
  } = props.data;

  const priceFloat = Number.parseFloat(priceDecimal?.toString()).toFixed(2);
  const priceString = maskCurrency(priceFloat, currency as any);

  payload.value = {
    ...payload.value,
    name,
    description,
    price: priceString as any,
    frequency,
    free,
    active,
    currency,
  };
}

function maskMoney(data: string) {
  return maskCurrency(data, payload.value.currency);
}

function changeCurrency() {
  payload.value.price = maskMoney(payload.value.price);

  emit("update:data", {
    price: payload.value.price,
    currency: payload.value.currency,
  });
}

watch(
  () => props.data,
  () => {
    resumeData();
  },
  { immediate: true }
);
</script>

<template>
  <article class="form-plan">
    <section class="group-name">
      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        required
        maxlength="75"
        @blur="update('name')"
      >
        <template #label> {{ t("common.labels.name") }} *</template>
        <template v-if="errors.name" #error> {{ errors.name }} </template>
      </base-input>

      <base-select
        v-model="payload.active"
        :value="payload.active"
        :options="activeOptions"
        :is-empty="false"
        @change="update('active')"
      >
        <template #label> {{ t("common.labels.active") }}</template>
      </base-select>

      <base-select
        v-model="payload.free"
        :value="payload.free"
        :options="freeOptions"
        :is-empty="false"
        @change="update('free')"
      >
        <template #label> {{ t("common.labels.free") }}</template>
      </base-select>
    </section>

    <section class="flex flex-row gap-4">
      <base-input
        v-model="payload.frequency"
        :value="payload.frequency"
        type="number"
        min="1"
        maxlength="4"
        required
        @blur="update('frequency')"
      >
        <template #label> {{ t("plan.labels.frequency") }} *</template>
        <template v-if="errors.frequency" #error>
          {{ errors.frequency }}
        </template>
      </base-input>

      <base-select
        v-model="payload.currency"
        :value="payload.currency"
        :options="currencyOptions"
        :disabled="payload.free"
        :is-empty="false"
        @change="changeCurrency()"
      >
        <template #label> {{ t("common.labels.currency") }}</template>
      </base-select>

      <base-input
        v-model="payload.price"
        :value="payload.price"
        :mask="maskMoney"
        :disabled="payload.free"
        @blur="update('price')"
      >
        <template #label> {{ t("common.labels.price") }} *</template>
        <template v-if="errors.price" #error> {{ errors.price }} </template>
      </base-input>
    </section>

    <base-textarea
      v-model="payload.description"
      :value="payload.description"
      :rows="5"
      @blur="update('description')"
    >
      <template #label>
        {{ t("common.labels.description") }}
      </template>
    </base-textarea>
  </article>
</template>

<style lang="scss" scoped>
.form-plan {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;

  .group-name {
    display: grid;
    grid-template-columns: 1fr 8rem 8rem;
    gap: 1rem;
  }
}
</style>
